import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { searchAdminListings } from "../api/adminApi";
import {
  StatusBadge, ImageHealthBadge as IHBadge, ShortId, fmtDate, Section,
} from "../components/admin/AdminUI";

const VERTICALS = ["", "auto", "immo"];
const HEALTHS = ["", "alive", "dead_on_check", "unchecked"];
const STATUSES = ["", "active", "inactive", "archived", "duplicate"];
const COUNTRIES = ["", "AT", "DE", "CH", "IT", "HR"];
const SOURCES = ["", "willhaben"];

function FilterSelect({ label, value, options, onChange, testid }) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-zinc-500">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5 text-zinc-200"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o || "any"}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function AdminListingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [filters, setFilters] = useState({
    vertical: searchParams.get("vertical") || "",
    country: searchParams.get("country") || "",
    source_platform: searchParams.get("source_platform") || "",
    image_health: searchParams.get("image_health") || "",
    status: searchParams.get("status") || "",
    has_mapping_warnings: searchParams.get("has_mapping_warnings") || "",
    created_after: searchParams.get("created_after") || "",
    created_before: searchParams.get("created_before") || "",
  });
  const [panelOpen, setPanelOpen] = useState(false);
  const [debouncedQ, setDebouncedQ] = useState(q);

  // Debounce freeform input by 400ms.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q), 400);
    return () => clearTimeout(id);
  }, [q]);

  // Sync into URL (deep-linkable).
  useEffect(() => {
    const sp = new URLSearchParams();
    if (debouncedQ) sp.set("q", debouncedQ);
    for (const [k, v] of Object.entries(filters)) {
      if (v !== "" && v != null) sp.set(k, v);
    }
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, filters]);

  const effectiveParams = () => {
    const out = { limit: 25 };
    if (debouncedQ) out.q = debouncedQ;
    for (const [k, v] of Object.entries(filters)) {
      if (v === "" || v == null) continue;
      if (k === "has_mapping_warnings") out[k] = v === "true";
      else out[k] = v;
    }
    return out;
  };

  const {
    data, fetchNextPage, hasNextPage, isFetchingNextPage,
    isLoading, isError, error,
  } = useInfiniteQuery({
    queryKey: ["admin", "listings", debouncedQ, filters],
    queryFn: ({ pageParam }) =>
      searchAdminListings({ ...effectiveParams(), cursor: pageParam }),
    initialPageParam: null,
    getNextPageParam: (last) => last.next_cursor || undefined,
  });

  const items = (data?.pages || []).flatMap((p) => p.items || []);
  const totalEstimate = data?.pages?.[0]?.total_estimate;
  const searchTimedOut = (data?.pages || []).some((p) => p.search_timeout);

  const clearFilters = () => {
    setFilters({
      vertical: "", country: "", source_platform: "",
      image_health: "", status: "", has_mapping_warnings: "",
      created_after: "", created_before: "",
    });
  };

  return (
    <div data-testid="admin-listings-page" className="space-y-6">
      <header>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
          /api/admin/listings
        </span>
        <h1 className="text-2xl text-zinc-100 font-medium mt-1">Listings search</h1>
      </header>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/50 p-4 space-y-4">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by ULID, AD_ID, URL, or title…"
          data-testid="admin-listings-search-input"
          className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-4 py-2 text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/60"
        />
        <button
          type="button"
          onClick={() => setPanelOpen((v) => !v)}
          data-testid="admin-listings-toggle-filters"
          className="text-xs text-zinc-400 hover:text-zinc-200"
        >
          {panelOpen ? "− hide filters" : "+ show filters"}
        </button>
        {panelOpen && (
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-zinc-900"
            data-testid="admin-listings-filter-panel"
          >
            <FilterSelect
              label="Vertical" value={filters.vertical} options={VERTICALS}
              onChange={(v) => setFilters((f) => ({ ...f, vertical: v }))}
              testid="filter-vertical"
            />
            <FilterSelect
              label="Country" value={filters.country} options={COUNTRIES}
              onChange={(v) => setFilters((f) => ({ ...f, country: v }))}
              testid="filter-country"
            />
            <FilterSelect
              label="Source" value={filters.source_platform} options={SOURCES}
              onChange={(v) => setFilters((f) => ({ ...f, source_platform: v }))}
              testid="filter-source"
            />
            <FilterSelect
              label="Image health" value={filters.image_health} options={HEALTHS}
              onChange={(v) => setFilters((f) => ({ ...f, image_health: v }))}
              testid="filter-image-health"
            />
            <FilterSelect
              label="Status" value={filters.status} options={STATUSES}
              onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
              testid="filter-status"
            />
            <FilterSelect
              label="Mapping warnings" value={filters.has_mapping_warnings}
              options={["", "true", "false"]}
              onChange={(v) => setFilters((f) => ({ ...f, has_mapping_warnings: v }))}
              testid="filter-warnings"
            />
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-zinc-500">Created after</span>
              <input
                type="datetime-local"
                value={filters.created_after}
                onChange={(e) => setFilters((f) => ({ ...f, created_after: e.target.value }))}
                className="bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5 text-zinc-200"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-zinc-500">Created before</span>
              <input
                type="datetime-local"
                value={filters.created_before}
                onChange={(e) => setFilters((f) => ({ ...f, created_before: e.target.value }))}
                className="bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1.5 text-zinc-200"
              />
            </label>
            <div className="col-span-full flex gap-2 pt-2">
              <button
                type="button"
                onClick={clearFilters}
                data-testid="admin-listings-clear-filters"
                className="px-3 py-1.5 text-xs rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Clear filters
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="text-xs text-zinc-500">
        {totalEstimate != null && (
          <span data-testid="admin-listings-total">
            ~{totalEstimate.toLocaleString("de-AT")} listings match
          </span>
        )}
        {searchTimedOut && (
          <span
            data-testid="admin-listings-search-timeout"
            className="text-amber-300"
          >
            Die Textsuche überschritt das Zeitbudget — bitte eine
            vollständige ULID, AD_ID oder URL eingeben (exakte Treffer
            laufen über den Index und antworten sofort).
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="text-zinc-500">Searching…</div>
      ) : isError ? (
        <div className="text-rose-400">Error: {String(error?.message || error)}</div>
      ) : items.length === 0 ? (
        <div data-testid="admin-listings-empty" className="text-sm text-zinc-500 border border-zinc-800 rounded-xl p-6 text-center">
          No listings match. Try clearing filters or broadening your search.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-zinc-950/50">
          <table className="w-full text-xs">
            <thead className="bg-zinc-900/60 text-zinc-500 uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">ULID</th>
                <th className="text-left px-3 py-2">Vert</th>
                <th className="text-left px-3 py-2">Title</th>
                <th className="text-right px-3 py-2">Price</th>
                <th className="text-left px-3 py-2">Country</th>
                <th className="text-left px-3 py-2">City</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Img</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Created</th>
                <th className="text-center px-3 py-2">⚠</th>
              </tr>
            </thead>
            <tbody className="text-zinc-300">
              {items.map((it) => (
                <tr key={it.ulid} className="border-t border-zinc-900/60 hover:bg-zinc-900/30">
                  <td className="px-3 py-2">
                    <Link to={`/admin/listings/${it.ulid}`} className="text-emerald-300 hover:underline">
                      <ShortId value={it.ulid} />
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-zinc-400">{it.vertical}</td>
                  <td className="px-3 py-2 max-w-md truncate" title={it.title}>{it.title || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{it.price_display || "—"}</td>
                  <td className="px-3 py-2">{it.country}</td>
                  <td className="px-3 py-2 text-zinc-500">{it.city || ""}</td>
                  <td className="px-3 py-2 text-zinc-400">{it.source_platform}</td>
                  <td className="px-3 py-2"><IHBadge value={it.image_health} /></td>
                  <td className="px-3 py-2"><StatusBadge status={it.status} /></td>
                  <td className="px-3 py-2 text-zinc-500">{fmtDate(it.created_at)}</td>
                  <td className="px-3 py-2 text-center">
                    {it.has_mapping_warnings ? (
                      <span title="has mapping warnings" className="text-amber-300">⚠</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasNextPage && (
        <div className="text-center pt-3">
          <button
            data-testid="admin-listings-load-more"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="px-4 py-1.5 rounded-md border border-zinc-700 text-zinc-200 text-sm hover:border-emerald-500/60 disabled:opacity-40"
          >
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
